import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    List,
    ListItem,
    ListItemText,
    Divider,
} from '@mui/material'
import { FormTemplate, FormFieldType } from '@/core/models/form.model'
import dayjs from 'dayjs'

interface FormSubmissionDialogProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    template: FormTemplate
    responses: Record<string, any>
    validationErrors: Record<string, string>
}

const FormSubmissionDialog: React.FC<FormSubmissionDialogProps> = ({
    open,
    onClose,
    onConfirm,
    template,
    responses,
    validationErrors,
}) => {
    const { t } = useTranslation()

    const formatValue = (value: any, type: FormFieldType): string => {
        if (value === null || value === undefined) return '-'

        switch (type) {
            case FormFieldType.Boolean:
                return value ? t('page.forms.fields.yes') : t('page.forms.fields.no')
            case FormFieldType.Number:
                return value.toString()
            case FormFieldType.Date:
                return dayjs(value).format('YYYY-MM-DD')
            case FormFieldType.DateTime:
                return dayjs(value).format('YYYY-MM-DD HH:mm')
            default:
                return value
        }
    }

    const getFieldResponse = (fieldId: string): string => {
        const field = template.fields.find(f => f.id === fieldId)
        if (!field) return '-'
        return formatValue(responses[fieldId], field.type as FormFieldType)
    }

    const missingRequiredFields = template.fields
        .filter(field => {
            if (field.type === FormFieldType.Boolean) {
                return false // Boolean fields are never "missing" since they default to false
            }
            return field.required && !responses[field.id]
        })
        .map(field => field.label)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('page.forms.submission.confirmDialog.title')}</DialogTitle>
            <DialogContent>
                {validationErrors && Object.keys(validationErrors).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="error">
                            <Typography variant="subtitle2" gutterBottom>
                                {t('page.forms.submission.confirmDialog.validationErrors')}
                            </Typography>
                            <List dense>
                                {Object.entries(validationErrors).map(([fieldId, error]) => {
                                    const field = template.fields.find(f => f.id === fieldId)
                                    return (
                                        <ListItem key={fieldId}>
                                            <ListItemText
                                                primary={field?.label || fieldId}
                                                secondary={error}
                                            />
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </Alert>
                    </Box>
                )}

                {missingRequiredFields.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="warning">
                            <Typography variant="subtitle2" gutterBottom>
                                {t('page.forms.submission.confirmDialog.missingFields')}
                            </Typography>
                            <ul>
                                {missingRequiredFields.map((field, index) => (
                                    <li key={index}>{field}</li>
                                ))}
                            </ul>
                        </Alert>
                    </Box>
                )}

                <Typography variant="subtitle1" gutterBottom>
                    {t('page.forms.submission.confirmDialog.summary')}
                </Typography>

                <List>
                    {template.fields.map((field, index) => (
                        <React.Fragment key={field.id}>
                            {index > 0 && <Divider />}
                            <ListItem>
                                <ListItemText
                                    primary={field.label}
                                    secondary={getFieldResponse(field.id)}
                                />
                            </ListItem>
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button
                    data-testid="form-confirm-submit"
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                    disabled={Object.keys(validationErrors).length > 0 || missingRequiredFields.length > 0}
                >
                    {t('page.forms.submission.confirmDialog.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default FormSubmissionDialog